import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiErrorDetail } from "@/lib/api";
import { ScanBarcode, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

const TILE_BG =
  "https://images.unsplash.com/photo-1719597677191-b4a8e8127d5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjZXJhbWljJTIwdGlsZXMlMjBwYXR0ZXJufGVufDB8fHx8MTc3NzY0NDA0MXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@loseria.com");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    if (user && user !== false) {
      const dest = user.role === "admin" ? "/admin" : "/pos";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bienvenido, ${u.name}`);
      const from = location.state?.from?.pathname;
      const dest = from || (u.role === "admin" ? "/admin" : "/pos");
      navigate(dest, { replace: true });
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Error al iniciar sesión";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-bone">
      {/* Left: ceramic pattern */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url(${TILE_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-stone/65" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-bone w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-clay flex items-center justify-center font-outfit font-bold text-2xl text-white shadow-[0_4px_14px_rgba(200,90,64,0.6)]">
              L
            </div>
            <div>
              <div className="font-outfit font-bold text-2xl">Losería</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-bone/60 mt-0.5">
                Sistema POS
              </div>
            </div>
          </div>
          <div>
            <h1 className="font-outfit text-5xl xl:text-6xl font-bold leading-[1.05] mb-6">
              Cada azulejo,
              <br />
              <span className="text-clay">cada venta</span>,
              <br />
              en su lugar.
            </h1>
            <p className="text-bone/70 max-w-md leading-relaxed">
              Punto de venta diseñado para tiendas de losería, con escaneo de código
              de barras, control de inventario y reportes en tiempo real.
            </p>
          </div>
          <div className="text-xs text-bone/40 uppercase tracking-[0.25em]">
            v1.0 · Hecho para mostradores rápidos
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-clay flex items-center justify-center font-outfit font-bold text-2xl text-white">
              L
            </div>
            <div>
              <div className="font-outfit font-bold text-2xl text-stone">Losería</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-stone/50 mt-0.5">
                Sistema POS
              </div>
            </div>
          </div>

          <div className="mb-10 animate-fade-in">
            <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold mb-3">
              Iniciar sesión
            </div>
            <h2 className="font-outfit text-4xl font-bold text-stone leading-tight">
              Bienvenido<br />de vuelta
            </h2>
            <p className="text-stone/60 mt-3 font-figtree">
              Ingresa con tu cuenta para abrir caja.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-stone/70 mb-2 block">
                Correo
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone/40" />
                <Input
                  ref={emailRef}
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-11 bg-white border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0 rounded-lg text-base"
                  placeholder="tu@loseria.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-stone/70 mb-2 block">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone/40" />
                <Input
                  type="password"
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-11 bg-white border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0 rounded-lg text-base"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                className="text-sm bg-clay/10 border border-clay/20 text-clay-hover rounded-lg p-3"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full h-12 bg-clay hover:bg-clay-hover text-white font-medium rounded-lg shadow-[0_4px_14px_rgba(200,90,64,0.39)] hover:-translate-y-0.5 transition-all"
            >
              <ScanBarcode className="w-4 h-4 mr-2" />
              {submitting ? "Entrando..." : "Iniciar sesión"}
            </Button>
          </form>

          <div className="mt-10 p-4 rounded-xl bg-sand border border-warm-border">
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone/50 font-semibold mb-2">
              Cuentas demo
            </div>
            <div className="space-y-1 text-xs text-stone/70 font-mono">
              <div>admin@loseria.com / admin123</div>
              <div>cajero@loseria.com / cajero123</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
